import { Box, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { ChartLoading, ChartNoData, generalizeData, genericChartOptions, useChartUtilsWithUserTime } from "../databases/metricsChartCommon";
import Chart from 'react-apexcharts'
import { merge } from "lodash";
import { LineChartWithPairsResponseDto } from "~/gen/api/metrics";
import { DeploymentResponseDto } from "~/gen/api/deploy";


const percentageFormatter = (value:number) => { 
  if (value === 0) return '0%'
  if (!value) return ''
  const surfix = '%'
  return `${Number.isInteger(value) ? value : value.toFixed(1)}${surfix}` 
}

const sizeFormatter = (value:number) => { 
  if (value === 0) return '0 GB'
  if (!value) return ''
  const surfix = ' GB'
  const num = value / 1024 / 1024 / 1024
  return `${Number.isInteger(num) ? num : num.toFixed(1)}${surfix}` 
}

const CpuMemChart: FC<{
  cpuLineData?: LineChartWithPairsResponseDto,
  memLineData?: LineChartWithPairsResponseDto,
  currDatabase?: DeploymentResponseDto,
  loading?: boolean;
}> = ({
  cpuLineData,
  memLineData,
  currDatabase,
  loading,
}) => {
  const theme = useTheme();
  const { pairWithUserTime } = useChartUtilsWithUserTime()

  const finalData = useMemo(() => {
    const cpuNMemData: LineChartWithPairsResponseDto = {
      legend: [...(cpuLineData ? ['Percentage CPU (Avg)'] : []), ...(memLineData ? ['Available Memory (Avg)'] : [])],
      series: [
        ...(cpuLineData?.series || []).map(s => ({
          name: s.name,
          data: s.data && s.data.map(pairWithUserTime)
        })), 
        ...(memLineData?.series || []).map(s => ({
          name: s.name,
          data: s.data && s.data.map(pairWithUserTime)
        })),
      ]
    }
  
    return generalizeData(theme, cpuNMemData, ['#388E3C', '#F57C00'])
  }, [theme, cpuLineData, memLineData])

  const genericOptions = useMemo(() => genericChartOptions(theme), [theme])
  
  const chart = useMemo(() => {
  
    const maxMemorySize = (() => {
      if (!currDatabase?.hostingSpecification) return 0;
      const m = /(\d)+ GB/.exec(currDatabase.hostingSpecification)
      if (!m) return undefined
      return Number(m[0].replace(' GB', '')) * 1024 * 1024 * 1024
    })();

    const erase = {
      options: {
        markers: null,
      },
      yaxis: null,
    }
    const patch = {
      options: {
        markers: { size: 0 },
        yaxis: [
          {
            seriesName: 'Percentage CPU (Avg)',
            max: 100,
            title: {
              style: {
                fontWeight: '400',
              },
            },
            axisBorder: {
              color: theme.palette.divider,
              show: true
            },
            axisTicks: {
              color: theme.palette.divider,
              show: true
            },
            labels: {
              style: {
                colors: theme.palette.text.secondary
              }
            }
          },
          {
            seriesName: 'Available Memory (Avg)',
            opposite: true,
            max: maxMemorySize,
            title: {
              style: {
                fontWeight: '400',
              },
            },
            axisBorder: {
              color: theme.palette.divider,
              show: true
            },
            axisTicks: {
              color: theme.palette.divider,
              show: true
            },
            labels: {
              style: {
                colors: theme.palette.text.secondary
              }
            }
          },
        ]
      },
    }
    return merge({}, genericOptions, {
      options: {
        chart: {
          animations: { enabled: false }
        },
        colors: finalData.series.map((item) => item.color),
        tooltip: {
          x: { format: 'dd MMM / HH:mm' }
        },
        xaxis: {
          type: 'datetime',
        },
        yaxis: [{
          labels: {
            formatter: percentageFormatter,
          },
        },{
          labels: {
            formatter: sizeFormatter,
          },
        }],
        legend: { show: true },
      },
      series: finalData.series.map(s => ({
        ...s,
        data: s.data
      })),
    }, erase, patch)
  }, [genericOptions, finalData.series, finalData]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <Box
      sx={{
        position: 'relative'
      }}
    >
      <Chart
        height="350"
        type="line"
        {...chart}
      />
      <ChartNoData show={!loading && finalData.series.length === 0} />
      <ChartLoading show={loading} />
    </Box>
  )
}

export default CpuMemChart;
